import { CATEGORIES, DEF, DEVICES } from "../../plc-sim/library";
import { store, useTick } from "../../plc-sim/store";
import { WIRE_COLORS } from "../../plc-sim/types";

export function DevicePalette({
  onAdd,
  wireColorId,
  setWireColorId,
}: {
  onAdd: (type: string) => void;
  wireColorId: string;
  setWireColorId: (c: string) => void;
}) {
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div>
        <p className="mb-1.5 text-[11px] font-semibold text-muted-foreground">لون السلك</p>
        <div className="flex flex-wrap gap-1.5">
          {WIRE_COLORS.map((c) => (
            <button
              key={c.id}
              title={c.label}
              onClick={() => setWireColorId(c.id)}
              className="h-7 w-7 rounded-full border-2"
              style={{
                background: c.css,
                borderColor: wireColorId === c.id ? "var(--color-primary)" : "var(--color-border)",
              }}
            />
          ))}
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const items = DEVICES.filter((d) => d.cat === cat.id);
        if (!items.length) return null;
        return (
          <div key={cat.id}>
            <p className="mb-1.5 text-[11px] font-semibold text-muted-foreground">{cat.label}</p>
            <div className="grid grid-cols-2 gap-1.5">
              {items.map((d) => (
                <button
                  key={d.type}
                  onClick={() => onAdd(d.type)}
                  className="rounded-md border border-border bg-card px-2 py-2 text-right text-[11px] hover:border-primary hover:bg-secondary"
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Properties({
  selected,
  onDelete,
}: {
  selected: string | null;
  onDelete: () => void;
}) {
  useTick();
  const d = store.project.devices.find((x) => x.id === selected);
  if (!d)
    return (
      <div className="p-3 text-[11px] text-muted-foreground">
        اختر قطعة من لوحة العمل لعرض خصائصها.
      </div>
    );
  const def = DEF[d.type];

  return (
    <div className="space-y-3 p-3 text-xs">
      <div>
        <p className="mb-1 text-[11px] text-muted-foreground">الاسم</p>
        <input
          className="sim-input w-full"
          value={d.label}
          onChange={(e) =>
            store.update(() => {
              d.label = e.target.value;
            }, false)
          }
        />
      </div>
      <p className="text-[11px] text-muted-foreground">النوع: {def?.name}</p>

      {d.type === "timer" && (
        <div className="space-y-2 rounded-md border border-border bg-secondary/35 p-2">
          <div>
            <p className="mb-1 text-[11px] text-muted-foreground">الزمن (ثانية)</p>
            <input
              className="sim-input w-full font-mono"
              type="number"
              min="0"
              step="0.1"
              value={d.props["preset"] ?? "3"}
              onChange={(e) =>
                store.update(() => {
                  d.props["preset"] = e.target.value;
                }, false)
              }
            />
          </div>
          <div className="text-[10px] leading-5 text-muted-foreground">
            <div><b>التغذية:</b> 2 = A1 ، 7 = A2 — يعمل على 24V أو 220V بالمحاكي.</div>
            <div><b>COM:</b> 1 و 8</div>
            <div><b>NC:</b> 3 و 6</div>
            <div><b>NO:</b> 4 و 5</div>
            <div><b>المجموعات:</b> 1→NC3/NO4 ، 8→NC6/NO5</div>
          </div>
        </div>
      )}

      {d.type === "relay" && (
        <div className="space-y-1 rounded-md border border-border bg-secondary/35 p-2 text-[10px] leading-5 text-muted-foreground">
          <div className="text-[11px] font-semibold text-foreground">خصائص الريليه 14 Pin</div>
          <div><b>الملف:</b> 13 = A1 ، 14 = A2</div>
          <div><b>المجموعة 1:</b> 5 COM → 1 NC / 9 NO</div>
          <div><b>المجموعة 2:</b> 6 COM → 2 NC / 10 NO</div>
          <div><b>المجموعة 3:</b> 7 COM → 3 NC / 11 NO</div>
          <div><b>المجموعة 4:</b> 8 COM → 4 NC / 12 NO</div>
        </div>
      )}

      {d.type === "overload" && (
        <div className="space-y-1 rounded-md border border-border bg-secondary/35 p-2 text-[10px] leading-5 text-muted-foreground">
          <div className="text-[11px] font-semibold text-foreground">خصائص الأوفرلود Schneider TeSys LRD06</div>
          <div><b>الباور:</b> L1→T1 ، L2→T2 ، L3→T3</div>
          <div><b>NC حماية:</b> 95 → 96</div>
          <div><b>NO إنذار:</b> 97 → 98</div>
          <div><b>STOP:</b> يفصل الأوفرلود (Trip)</div>
          <div><b>RESET:</b> يعيد الأوفرلود للوضع الطبيعي</div>
        </div>
      )}

      {d.type === "lamp" && (
        <div>
          <p className="mb-1 text-[11px] text-muted-foreground">اللون</p>
          <select
            className="sim-input w-full"
            value={d.props["color"] ?? "green"}
            onChange={(e) =>
              store.update(() => {
                d.props["color"] = e.target.value;
              })
            }
          >
            <option value="green">أخضر</option>
            <option value="red">أحمر</option>
            <option value="amber">برتقالي</option>
            <option value="blue">أزرق</option>
          </select>
        </div>
      )}

      {(d.type === "motor" || d.type === "motor_star_delta") && (
        <div className="space-y-2 rounded-md border border-border bg-secondary/35 p-2">
          <div>
            <p className="mb-1 text-[11px] text-muted-foreground">اتجاه دوران الماطور</p>
            <div className="rounded border border-border bg-card px-2 py-1.5 font-semibold">
              {d.state["direction"] === "forward"
                ? "→ FORWARD — مع عقارب الساعة"
                : d.state["direction"] === "reverse"
                  ? "← REVERSE — عكس عقارب الساعة"
                  : "متوقف — بانتظار توصيل 3 فازات صحيح"}
            </div>
          </div>
          <div>
            <p className="mb-1 text-[11px] text-muted-foreground">ترتيب الفازات على أطراف الماطور</p>
            <div className="sim-chip w-fit font-mono">{String(d.state["phaseOrder"] ?? "---")}</div>
          </div>
          <p className="text-[10px] leading-5 text-muted-foreground">
            إذا بدّلت أي فيزين من الفازات الثلاثة، اتجاه الدوران يتغيّر تلقائياً بين FORWARD و REVERSE.
          </p>
        </div>
      )}

      {d.type === "motor_star_delta" && (
        <div className="space-y-2 rounded-md border border-border bg-card p-2">
          <p className="text-[11px] font-semibold">تعليمات ربط STAR / DELTA</p>
          <div className="rounded border border-border bg-secondary/40 p-2 leading-5">
            <span className="font-semibold">ستار STAR (Y):</span>
            <br />
            أوصل الفازات إلى U1 و V1 و W1، ثم اجمع U2 + V2 + W2 مع بعض.
          </div>
          <div className="rounded border border-border bg-secondary/40 p-2 leading-5">
            <span className="font-semibold">دلتا DELTA (Δ):</span>
            <br />
            اربط U1 مع W2، و V1 مع U2، و W1 مع V2، ثم أوصل الفازات الثلاثة إلى نقاط الربط الثلاثة.
          </div>
          <div>
            <p className="mb-1 text-[11px] text-muted-foreground">الربط المكتشف حالياً</p>
            <div className="font-semibold">
              {d.state["connection"] === "star"
                ? "STAR (Y) — ستار"
                : d.state["connection"] === "delta"
                  ? "DELTA (Δ) — دلتا"
                  : "الربط غير مكتمل"}
            </div>
          </div>
        </div>
      )}

      <div>
        <p className="mb-1 text-[11px] text-muted-foreground">الأطراف / Terminals</p>
        <div className="flex flex-wrap gap-1">
          {def?.terminals.slice(0, 40).map((t) => (
            <span key={t.id} className="sim-chip font-mono">
              {t.label}
            </span>
          ))}
        </div>
      </div>

      <div className="flex gap-1.5">
        <button className="sim-btn" onClick={() => store.duplicateDevice(d.id)}>
          نسخ
        </button>
        <button className="sim-btn text-destructive" onClick={onDelete}>
          حذف
        </button>
      </div>
    </div>
  );
}
